import React from 'react';
// template components
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';

import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';

export default () => (
  <SectionContainer
    id='life-insurance-faq-premium-payments'
    backgroundVariant='lightB'
    themeType='lightTheme'>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          component='h2'
          nextElement='typography'
          align='center'>
          Premium Payments
        </Typography>
        <Typography nextElement='typography'>
          Paying premiums keeps your insurance policies in force and ensures your beneficiaries will receive
          the level of protection you planned for them.
        </Typography>
        <Typography nextElement='typography'>
          Paying premiums has other advantages. It builds tax&#45;deferred cash value, which provides money for
          emergencies, your children&#39;s education, a dream vacation or supplemental retirement income.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Ways to Pay Your Premiums
        </Typography>
        <Typography nextElement='typography'>
          Northwestern Mutual offers four easy ways to pay your premium.
        </Typography>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Automatic withdrawal</strong>
                <BulletedList
                  bulletedListData={[
                    {
                      bulletText: <>Automatic withdrawal, often called Electronic Funds Transfer (EFT), is the quickest, most convenient way to pay your premiums. Just tell us what checking or savings account you want the money to come from, and how often you want to pay. You can pay your premiums monthly, quarterly, every six months or once a year. To pay by automatic withdrawal, log on to &#39;<Link
                        id='automatic-withdrawl-access-accounts-link'
                        linkUrl='https://plan.northwesternmutual.com/'
                        variant='primaryDeemphasize'>
                        Access Your Accounts
                      </Link>,&#39; select &#39;Billing Account&#39;, and complete the Automatic EFT Payments Form. You must have a Billing&#47;Payment Account in order to make payments via Automatic withdrawal.</>,
                    },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Online Payments</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: <>Northwestern Mutual&#39;s Online Payment option is safe, secure, and available 24&#47;7. You have the ability to schedule one	&#45;time EFT payments from your checking or savings account. Online payments can be scheduled in advance with the effective date you select, up to the due date. To pay online, log on through &#39;Manage Your Accounts&#39; and select &#39;Pay Online.&#39; You must have a Billing Account in order to access the Pay Online feature.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Direct Billing</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: 'Northwestern Mutual sends you a billing notice in the mail. If you have several policies with Northwestern Mutual, you can have them placed on one bill using a Billing Account.' },
                    { bulletText: <><strong>Note:</strong> A separate Billing Account is required for variable products and annuity products.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Electronic Payment Services</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: 'Not to be confused with EFT, these are outside electronic bill payment services such as MSMoney, Quicken or your bank. Northwestern Mutual still bills you directly, but you instruct the outside service to pay. An electronic payment service arrangement is an agreement between you and the provider of the service. Unlike EFT, where Northwestern Mutual draws the amount for the payment on the day it is due, you are responsible for coordinating the payment amount, date and payee with the service.' },
                    { bulletText: <><strong>Tip:</strong> When paying through an electronic payment service, use your Billing Account or policy number. If the service does not ask you to enter your first and last name separately, enter your last name first.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <Typography
          component='h3'
          nextElement='typography'>
          When You Don&#39;t Pay Your Premiums
        </Typography>
        <Typography nextElement='typography'>
          If your premium payments are not automatically withdrawn from your bank account,
          Northwestern Mutual should receive payment by the due date on your bill. If the insured person dies
          during the grace period, the unpaid premium is deducted from the death benefit.
        </Typography>
        <Typography nextElement='typography'>
          If your premium is not paid within the grace period and your policy has built cash value, you may be
          able to take advantage of your policy&#39;s automatic premium loan option which you selected at the time of policy issue. These options pay your premium and keep your policy in force for a specified time. Policies that do not have cash value, such as term insurance and disability insurance, will lapse
          when the grace period expires.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Available Options
        </Typography>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Automatic Premium Loan &#40;APL&#41;</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: <>Any unpaid premium is automatically paid with a premium loan at the end of the grace period. Premium loans accrue interest according to the rate in your policy.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Extended Term Coverage</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: <>Your policy&#39;s cash value is used to purchase term insurance that is equal to the death benefit of your existing policy. Your policy will terminate at the end of the term insurance period and will not have any value. This option applies only to whole life policies, excluding Variable CompLife.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <BulletedList
          bulletedListData={[
            {
              bulletText: (<>
                <strong>Paid Up Contract</strong>
                <BulletedList
                  bulletedListData={[
                    { bulletText: <>If your contract is paid up, you do not need to make future premium payments to retain coverage. Limited&#45;pay policies become paid up after the required number of premiums have been paid. The term also refers to the end result of a non&#45;forfeiture option that causes a policy to convert to a smaller, but still participating, completely paid up policy.</> },
                  ]}
                  bulletType='disc' />
              </>),
            },
          ]}/>
        <Typography
          component='h3'
          nextElement='typography'>
          Changing Bank Names&#47;Payment Date
        </Typography>
        <Typography nextElement='typography'>
          To change the date the premium is automatically withdrawn from your bank, or to let us know
          you have changed banks:
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Contact your Northwestern Mutual representative' },
            { bulletText: <>Sign&#45;up or Log in to your Billing Account</> },
            {
              bulletText: <>Northwestern Mutual directly at <Link
                id='life-insurance-faq-toll-free-number-link'
                ariaLabel='Call to change the date of premium withdrawl'
                linkUrl='tel:8003888123'
                variant='primaryDeemphasize'>
                1&#45;800&#45;388&#45;8123
              </Link>, <time dateTime='7:00-06:00'>7am</time>&#8211;<time dateTime='18:00-06:00'>6pm</time> CT, Monday &#8211; Friday</>,
            },
          ]}
        />
        <Typography nextElement='typography'>
          To change the payment date, you will need to provide:
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Your name' },
            { bulletText: 'Your Billing Account or policy numbers' },
            { bulletText: 'Your bank account number' },
            { bulletText: 'The current date of your automatic withdrawal' },
            { bulletText: 'The new date of your automatic withdrawal' },
          ]}
        />
        <Typography nextElement='typography'>
          To change the bank where payments are withdrawn:
        </Typography>
        <BulletedList
          bulletedListData={[
            { bulletText: 'Your name' },
            { bulletText: 'Your Billing Account or policy numbers' },
            { bulletText: <>Your bank transit&#47;routing number</> },
            { bulletText: 'Your new bank account number' },
            { bulletText: 'The name of your new bank' },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
